import { useEffect, useState } from "react";

import { debug } from "@/utils/debug";
import { bootstrapEntraSession } from "@/msal/bootstrap-entra-session";

/**
 * After Statsig is ready and Redux persist has rehydrated: bootstraps the Entra
 * session from MSAL cache (if applicable) so route guards see `isAuthorized`
 * before the router mounts.
 *
 * Mirrors NM's `useMsalAppReady`.
 */
export const useMsalAppReady = (canInitialize: boolean): boolean => {
  const [msalReady, setMsalReady] = useState(false);

  useEffect(() => {
    if (!canInitialize) {
      debug.log(
        "[msal-ready] Waiting: canInitialize=false (persist/statsig not ready)"
      );
      return;
    }

    const pipeline = { superseded: false };

    void (async () => {
      debug.log("[msal-ready] Running bootstrapEntraSession");
      await bootstrapEntraSession({
        isSuperseded: () => pipeline.superseded
      });

      if (pipeline.superseded) {
        debug.log("[msal-ready] Cancelled before setMsalReady");
        return;
      }

      debug.log("[msal-ready] setMsalReady(true)");
      setMsalReady(true);
    })();

    return () => {
      debug.log("[msal-ready] Cleanup — cancel bootstrap pipeline");
      pipeline.superseded = true;
      setMsalReady(false);
    };
  }, [canInitialize]);

  return canInitialize && msalReady;
};
